import React, { useState, useEffect } from 'react';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'https://peekr-production.up.railway.app';

const errStyle = { color: '#FF4466', fontSize: 13, marginBottom: 12, textAlign: 'center', maxWidth: 320 };

export default function JoinScreen({ onJoin, error: serverError, initialCode }) {
  // Deep links (/join/CODE) already have the code → start on the details step.
  const [step, setStep] = useState(initialCode ? 'details' : 'code');
  const [code, setCode] = useState(initialCode || '');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  // Room meta fetched before joining so the details step can present itself as
  // a celebration guestbook (wedding/party) or a buyer lead form (property
  // showing). null = unknown (room not live yet / fetch failed) → neutral copy.
  const [meta, setMeta] = useState(null);

  useEffect(() => { if (serverError) setError(serverError); }, [serverError]);

  useEffect(() => {
    if (step !== 'details' || !code) return;
    let stale = false;
    fetch(`${SERVER_URL}/gallery/${code.trim().toUpperCase()}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!stale && d) setMeta({ celebration: !!d.celebration, eventName: d.eventName || '' });
      })
      .catch(() => {});
    return () => { stale = true; };
  }, [step, code]);

  const celebration = !!meta?.celebration;
  const knownProperty = meta != null && !meta.celebration;

  const submitCode = (e) => {
    e.preventDefault();
    const clean = code.trim().toUpperCase();
    if (clean.length < 5) { setError('Invite codes are at least 5 characters — e.g. MAPLE12'); return; }
    setError('');
    setCode(clean);
    setStep('details');
  };

  const submitDetails = (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!phone.trim() && !email.trim()) {
      setError(celebration
        ? 'Add a phone or email so they can send you the album after.'
        : knownProperty
          ? 'Add a phone or email so the agent can reach you.'
          : 'Please add a phone or email.');
      return;
    }
    setError('');
    onJoin(code.trim().toUpperCase(), { name: name.trim(), phone: phone.trim(), email: email.trim() }, meta);
  };

  // ── Step 2: guestbook (celebration) / lead capture (property) ─────────────
  if (step === 'details') {
    const heading = celebration ? "You're invited" : "You're almost in";
    const emoji = celebration ? '🎉' : '👋';
    const tagline = celebration ? (
      <>Sign the guestbook{meta?.eventName ? <> for<br />{meta.eventName}</> : ''} — so the family<br />knows you were part of it.</>
    ) : knownProperty ? (
      <>Leave your details so the agent<br />can follow up after the showing.</>
    ) : (
      <>Leave your name so your host<br />knows who's watching.</>
    );
    const buttonLabel = celebration ? 'Join the celebration →' : knownProperty ? 'Enter the showing →' : 'Join →';
    const subLine = celebration
      ? `${meta?.eventName || code} · No app needed · Phone or email is fine`
      : `Showing ${code} · No account needed · Phone or email is fine`;

    return (
      <div className="screen">
        <div style={{ fontSize: 52, marginBottom: 12 }}>{emoji}</div>
        <div className="brand">{heading}</div>
        <div className="tagline">{tagline}</div>

        <form onSubmit={submitDetails} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <input className="input" type="text" placeholder="Your name" value={name}
            onChange={(e) => { setName(e.target.value); setError(''); }}
            autoFocus autoComplete="name" maxLength={80} />
          <input className="input" type="tel" placeholder="Phone number" value={phone}
            onChange={(e) => { setPhone(e.target.value); setError(''); }}
            autoComplete="tel" maxLength={30} />
          <input className="input" type="email" placeholder="Email address" value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); }}
            autoComplete="email" maxLength={120} />

          {error && <div style={errStyle}>{error}</div>}

          <button className="btn" type="submit">{buttonLabel}</button>
        </form>

        <div className="sub" style={{ marginTop: 18 }}>{subLine}</div>
      </div>
    );
  }

  // ── Step 1: invite code (neutral — works for showings and celebrations) ────
  return (
    <div className="screen">
      <div style={{ fontSize: 52, marginBottom: 12 }}>👁</div>
      <div className="brand">Peekr</div>
      <div className="tagline">
        Enter your invite code<br />to watch live.
      </div>

      <form onSubmit={submitCode} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <input
          className="input"
          type="text"
          placeholder="e.g. MAPLE12"
          value={code}
          onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(''); }}
          maxLength={20}
          autoFocus
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck="false"
        />

        {error && <div style={errStyle}>{error}</div>}

        <button className="btn" type="submit">Continue</button>
      </form>

      {/* How it works — for first time visitors */}
      <div className="card" style={{ marginTop: 40, padding: '24px 20px', maxWidth: 340, width: '100%', textAlign: 'left' }}>
        <div className="eyebrow" style={{ marginBottom: 16 }}>How it works</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            ['📱', 'Your host opens Peekr on their phone'],
            ['🔑', 'An invite code is generated instantly'],
            ['👁', 'You type the code here — no app needed'],
            ['📹', 'They go live — you see everything instantly'],
          ].map(([icon, text]) => (
            <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 20 }}>{icon}</span>
              <span style={{ fontSize: 14, color: '#AABCCC', lineHeight: 1.4 }}>{text}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="sub" style={{ marginTop: 20 }}>No account needed · Works on any device</div>
    </div>
  );
}
